/**
 * chart-theme — the ONE style a Recharts tooltip in the web app is styled with
 * (AUT-F0-T2, contract C2). No `<Tooltip>` passes its own contentStyle,
 * labelStyle or itemStyle.
 *
 * Before this file, fourteen object-literal tooltip styles each spelled their
 * own tooltip, and every one of them wrapped a colour function round a theme
 * variable the Tailwind v4 theme no longer defines (the unprefixed --border and
 * --popover). A `border` shorthand with an invalid `var()` is
 * invalid at computed-value time, so those tooltips had NO border while
 * Recharts' own `#fff` background stayed — a white box in dark mode — and the
 * Connections ones computed a transparent background.
 *
 * Every colour here is a `TOKENS` entry (lib/design-tokens.ts), i.e. a
 * `var(--color-*)` reference the theme defines in both light and dark, so the
 * tooltip follows the mode and any tenant branding override.
 *
 * Use it by spreading it onto the Recharts tooltip — never by passing
 * `contentStyle` / `labelStyle` / `itemStyle` yourself:
 *
 *   <Tooltip {...CHART_TOOLTIP} labelFormatter={...} />
 *
 * `itemStyle` deliberately carries no colour: Recharts colours each row with
 * its series colour, which is how a reader matches a value to its line or bar.
 *
 * Guarded by lib/__tests__/chart-tooltip-one-style.test.ts and, for the theme
 * variables themselves, scripts/ci/theme-token-lint.mjs.
 */

import type { CSSProperties } from 'react';

import { TOKENS } from './design-tokens';

type ChartTooltipStyle = {
  readonly contentStyle: CSSProperties;
  readonly labelStyle: CSSProperties;
  readonly itemStyle: CSSProperties;
};

export const CHART_TOOLTIP: ChartTooltipStyle = {
  contentStyle: {
    backgroundColor: TOKENS.popover,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 6,
    color: TOKENS.popoverFg,
    fontSize: 11,
    padding: '6px 8px',
  },
  labelStyle: {
    color: TOKENS.popoverFg,
    fontWeight: 500,
    marginBottom: 2,
  },
  itemStyle: {
    paddingTop: 1,
    paddingBottom: 1,
  },
};
